/**
 * Raison pour laquelle un essai ne s'est pas transformé en abonnement payant (A3 de la revue
 * données du 19/09/2026). Saisie par l'équipe dans la fiche admin du pro, typiquement après
 * un appel de fin d'essai. Une liste fermée plutôt qu'un texte libre : c'est ce qui permet de
 * compter les raisons dans Metabase ; la note libre porte le détail.
 */
export enum NonConversionReason {
  /** Trop cher, ou prix au-dessus de ce qu'il paie ailleurs. */
  PRICE = 'PRICE',
  /** N'a pas eu le temps de configurer ou de tester. */
  NO_TIME = 'NO_TIME',
  /** N'en voit pas l'utilité pour son activité. */
  NOT_NEEDED = 'NOT_NEEDED',
  /** Reste sur son outil actuel ou a choisi un concurrent. */
  ALREADY_EQUIPPED = 'ALREADY_EQUIPPED',
  /** Une fonctionnalité indispensable manque. */
  MISSING_FEATURE = 'MISSING_FEATURE',
  /** Bug ou difficulté technique qui l'a bloqué. */
  TECHNICAL_ISSUE = 'TECHNICAL_ISSUE',
  /** Trop compliqué à prendre en main. */
  TOO_COMPLEX = 'TOO_COMPLEX',
  /** A arrêté ou mis en pause son activité. */
  BUSINESS_STOPPED = 'BUSINESS_STOPPED',
  /** Injoignable : aucune réponse aux relances. */
  UNREACHABLE = 'UNREACHABLE',
  /** Autre raison, détaillée dans la note. */
  OTHER = 'OTHER',
  // Ajoutées avec la file d'appels (spec CRM du 30/09/2026), en fin d'enum.
  /** Est resté bloqué sur une étape précise (voir `CrmBlockedStep`). */
  BLOCKED_ON_STEP = 'BLOCKED_ON_STEP',
  /** Le produit ne fait pas ce qu'il croyait. */
  EXPECTATION_MISMATCH = 'EXPECTATION_MISMATCH',
  /** Pas assez de clients pour que ça vaille le coup. */
  NOT_ENOUGH_CUSTOMERS = 'NOT_ENOUGH_CUSTOMERS',
  /** N'a jamais vraiment eu l'intention d'essayer. */
  NO_INTENT = 'NO_INTENT',
  /** Le site ou l'aperçu ne lui plaisait pas. */
  PREVIEW_DISLIKED = 'PREVIEW_DISLIKED',
  /** N'a rien reçu, n'était pas au courant. */
  NOT_AWARE = 'NOT_AWARE',
  /** Trois reports d'appel sans conversation aboutie (posée par le système). */
  REPEATED_POSTPONE = 'REPEATED_POSTPONE',
}

/**
 * Raisons proposées à la saisie, dans l'ordre de l'écran (liste de la spec CRM, plus « activité
 * arrêtée »). `NOT_NEEDED` et `TECHNICAL_ISSUE` restent dans l'enum pour les données existantes
 * mais ne sont plus proposées ; `UNREACHABLE` et `REPEATED_POSTPONE` sont posées par le système.
 */
export const NON_CONVERSION_REASONS_SELECTABLE: readonly NonConversionReason[] = [
  NonConversionReason.NO_TIME,
  NonConversionReason.TOO_COMPLEX,
  NonConversionReason.BLOCKED_ON_STEP,
  NonConversionReason.EXPECTATION_MISMATCH,
  NonConversionReason.MISSING_FEATURE,
  NonConversionReason.PRICE,
  NonConversionReason.ALREADY_EQUIPPED,
  NonConversionReason.NOT_ENOUGH_CUSTOMERS,
  NonConversionReason.NO_INTENT,
  NonConversionReason.PREVIEW_DISLIKED,
  NonConversionReason.NOT_AWARE,
  NonConversionReason.BUSINESS_STOPPED,
  NonConversionReason.OTHER,
];

/** Raisons posées par le système, jamais proposées à la saisie. */
export const NON_CONVERSION_REASONS_SYSTEM: readonly NonConversionReason[] = [
  NonConversionReason.UNREACHABLE,
  NonConversionReason.REPEATED_POSTPONE,
];

/** Raisons qui exigent une note libre (« autre », et la fonctionnalité qui manque). */
export const NON_CONVERSION_REASONS_NOTE_REQUIRED: readonly NonConversionReason[] = [
  NonConversionReason.OTHER,
  NonConversionReason.MISSING_FEATURE,
];

/**
 * Hypothèse PLG que chaque raison met en cause (spec CRM §5). Affichée dans l'admin et recopiée
 * dans le modèle Metabase : c'est ce qui relie trente raisons classées aux hypothèses H1 à H8.
 */
export const NON_CONVERSION_REASON_HYPOTHESES: Partial<Record<NonConversionReason, readonly string[]>> = {
  [NonConversionReason.NO_TIME]: ['H6'],
  [NonConversionReason.TOO_COMPLEX]: ['H6'],
  [NonConversionReason.BLOCKED_ON_STEP]: ['H6'],
  [NonConversionReason.EXPECTATION_MISMATCH]: ['H2', 'H4'],
  [NonConversionReason.PRICE]: ['H7'],
  [NonConversionReason.ALREADY_EQUIPPED]: ['H1', 'H2'],
  [NonConversionReason.NOT_ENOUGH_CUSTOMERS]: ['H1'],
  [NonConversionReason.NO_INTENT]: ['H1'],
  [NonConversionReason.PREVIEW_DISLIKED]: ['H5'],
  [NonConversionReason.NOT_AWARE]: ['H8'],
};

/** Longueur max de la note libre saisie avec la raison. */
export const NON_CONVERSION_NOTE_MAX_LENGTH = 2000;
